/**
 * Fechas y zona horaria de ElDescuadre.
 *
 * DECISIÓN DE DISEÑO (A7 y A15 del plan): **todo se guarda en UTC** (columnas
 * `timestamptz`) y **todo se interpreta y se agrupa en hora de Colombia**.
 *
 * El error que corregimos: Cuentia agrupaba por mes usando la fecha en UTC, así
 * que un gasto registrado a las 20:00 en Colombia (01:00 UTC del día siguiente)
 * aparecía en el día siguiente y, el último día del mes, **en el mes siguiente**.
 *
 * Colombia no aplica horario de verano: su desfase es siempre UTC−5. Eso permite
 * convertir de forma exacta y sin ambigüedad, sin necesidad de una biblioteca de
 * zonas horarias.
 */
import { MoneyError } from './money.js';
/** Zona horaria oficial de la aplicación. */
export const APP_TIMEZONE = 'America/Bogota';
/** Desfase fijo de Colombia respecto a UTC, en minutos (UTC−5, sin horario de verano). */
export const COLOMBIA_UTC_OFFSET_MINUTES = -300;
/** Sufijo de desfase listo para construir fechas ISO: `-05:00`. */
export const COLOMBIA_OFFSET_SUFFIX = '-05:00';
const PARTS_FORMATTER = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});
const MONTH_NAMES_ES = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
];
const WEEKDAY_NAMES_ES = [
    'domingo',
    'lunes',
    'martes',
    'miércoles',
    'jueves',
    'viernes',
    'sábado',
];
function partValue(parts, type) {
    const found = parts.find((p) => p.type === type);
    if (!found)
        throw new MoneyError(`No se pudo leer la parte «${type}» de la fecha.`);
    // `hour` puede venir como "24" en algunos entornos para medianoche: se normaliza.
    const value = Number(found.value);
    return type === 'hour' && value === 24 ? 0 : value;
}
/** Convierte un instante a sus partes en hora de Colombia. */
export function toLocalParts(date) {
    if (Number.isNaN(date.getTime())) {
        throw new MoneyError('Fecha inválida.');
    }
    const parts = PARTS_FORMATTER.formatToParts(date);
    return {
        year: partValue(parts, 'year'),
        month: partValue(parts, 'month'),
        day: partValue(parts, 'day'),
        hour: partValue(parts, 'hour'),
        minute: partValue(parts, 'minute'),
        weekday: 0, // se calcula abajo
    };
}
/** Igual que `toLocalParts`, pero incluyendo el día de la semana. */
function localPartsWithWeekday(date) {
    const base = toLocalParts(date);
    const weekdayName = new Intl.DateTimeFormat('es-CO', {
        timeZone: APP_TIMEZONE,
        weekday: 'long',
    }).format(date);
    const index = WEEKDAY_NAMES_ES.indexOf(weekdayName.toLowerCase());
    return { ...base, weekday: index === -1 ? date.getUTCDay() : index };
}
function pad2(value) {
    return value < 10 ? `0${value}` : String(value);
}
/** Fecha local en formato `YYYY-MM-DD` (hora de Colombia). */
export function toLocalDateISO(date) {
    const p = toLocalParts(date);
    return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}
/** Hora local en formato `HH:mm` (hora de Colombia). */
export function toLocalTimeHHmm(date) {
    const p = toLocalParts(date);
    return `${pad2(p.hour)}:${pad2(p.minute)}`;
}
/** Clave de mes `YYYY-MM` (hora de Colombia). */
export function toMonthKey(date) {
    const p = toLocalParts(date);
    return `${p.year}-${pad2(p.month)}`;
}
/** Fecha de hoy en Colombia, `YYYY-MM-DD`. */
export function todayISO(now = new Date()) {
    return toLocalDateISO(now);
}
/** Mes actual en Colombia, `YYYY-MM`. */
export function currentMonthKey(now = new Date()) {
    return toMonthKey(now);
}
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_KEY_RE = /^(\d{4})-(\d{2})$/;
/** Valida y descompone una fecha `YYYY-MM-DD`. Lanza si es inválida o inexistente. */
export function parseDateISO(dateISO) {
    const match = ISO_DATE_RE.exec(dateISO);
    if (!match)
        throw new MoneyError(`Fecha inválida: «${dateISO}». Se espera YYYY-MM-DD.`);
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12)
        throw new MoneyError(`Mes inválido en «${dateISO}».`);
    if (day < 1 || day > daysInMonth(year, month)) {
        throw new MoneyError(`Día inválido en «${dateISO}».`);
    }
    return { year, month, day };
}
/** Valida y descompone una clave de mes `YYYY-MM`. */
export function parseMonthKey(monthKey) {
    const match = MONTH_KEY_RE.exec(monthKey);
    if (!match)
        throw new MoneyError(`Mes inválido: «${monthKey}». Se espera YYYY-MM.`);
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12)
        throw new MoneyError(`Mes inválido: «${monthKey}».`);
    return { year, month };
}
/** Número de días del mes indicado (mes 1-12). */
export function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/** Número de días del mes a partir de una clave `YYYY-MM`. */
export function daysInMonthOfKey(monthKey) {
    const { year, month } = parseMonthKey(monthKey);
    return daysInMonth(year, month);
}
/**
 * Construye el instante UTC correspondiente a una fecha y hora **de Colombia**.
 * Es la función que evita el error clásico de «fingir UTC».
 */
export function fromLocalDateTime(dateISO, timeHHmm = '12:00') {
    parseDateISO(dateISO);
    const timeMatch = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(timeHHmm);
    if (!timeMatch) {
        throw new MoneyError(`Hora inválida: «${timeHHmm}». Se espera HH:mm.`);
    }
    const hour = Number(timeMatch[1]);
    const minute = Number(timeMatch[2]);
    const second = timeMatch[3] ? Number(timeMatch[3]) : 0;
    if (hour > 23 || minute > 59 || second > 59) {
        throw new MoneyError(`Hora fuera de rango: «${timeHHmm}».`);
    }
    const instant = new Date(`${dateISO}T${pad2(hour)}:${pad2(minute)}:${pad2(second)}${COLOMBIA_OFFSET_SUFFIX}`);
    if (Number.isNaN(instant.getTime())) {
        throw new MoneyError(`No se pudo construir la fecha «${dateISO} ${timeHHmm}».`);
    }
    return instant;
}
/** Primer instante del día (00:00 en Colombia), en UTC. */
export function startOfLocalDay(dateISO) {
    return fromLocalDateTime(dateISO, '00:00');
}
/** Primer instante del día siguiente (00:00 en Colombia), en UTC. Es el límite exclusivo. */
export function endOfLocalDayExclusive(dateISO) {
    const { year, month, day } = parseDateISO(dateISO);
    const next = new Date(Date.UTC(year, month - 1, day + 1));
    return startOfLocalDay(`${next.getUTCFullYear()}-${pad2(next.getUTCMonth() + 1)}-${pad2(next.getUTCDate())}`);
}
/** Rango completo de un mes: `[inicio, finExclusivo)` en UTC. */
export function monthRange(monthKey) {
    const { year, month } = parseMonthKey(monthKey);
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    return {
        start: startOfLocalDay(`${year}-${pad2(month)}-01`),
        endExclusive: startOfLocalDay(`${nextYear}-${pad2(nextMonth)}-01`),
    };
}
/** Rango completo de un año: `[inicio, finExclusivo)` en UTC. */
export function yearRange(year) {
    return {
        start: startOfLocalDay(`${year}-01-01`),
        endExclusive: startOfLocalDay(`${year + 1}-01-01`),
    };
}
/** Suma (o resta) meses a una clave `YYYY-MM`. */
export function addMonths(monthKey, delta) {
    const { year, month } = parseMonthKey(monthKey);
    const zeroBased = month - 1 + delta;
    const newYear = year + Math.floor(zeroBased / 12);
    const newMonth = ((zeroBased % 12) + 12) % 12 + 1;
    return `${newYear}-${pad2(newMonth)}`;
}
/** Mes anterior a una clave `YYYY-MM`. */
export function previousMonthKey(monthKey) {
    return addMonths(monthKey, -1);
}
/** Compara dos claves de mes: negativo si `a` es anterior, 0 si son iguales. */
export function compareMonthKeys(a, b) {
    parseMonthKey(a);
    parseMonthKey(b);
    return a < b ? -1 : a > b ? 1 : 0;
}
/** Etiqueta legible de un mes: «octubre de 2026». */
export function formatMonthLabel(monthKey) {
    const { year, month } = parseMonthKey(monthKey);
    const name = MONTH_NAMES_ES[month - 1] ?? '';
    return `${name} de ${year}`;
}
/** Etiqueta corta de un mes: «oct 2026». */
export function formatMonthShort(monthKey) {
    const { year, month } = parseMonthKey(monthKey);
    const name = MONTH_NAMES_ES[month - 1] ?? '';
    return `${name.slice(0, 3)} ${year}`;
}
/** Etiqueta legible de una fecha: «jueves, 15 de octubre de 2026». */
export function formatDateLabel(dateISO) {
    const { year, month, day } = parseDateISO(dateISO);
    const name = MONTH_NAMES_ES[month - 1] ?? '';
    return `${day} de ${name} de ${year}`;
}
/** Etiqueta corta de una fecha: «15 oct». */
export function formatDateShort(dateISO) {
    const { month, day } = parseDateISO(dateISO);
    const name = MONTH_NAMES_ES[month - 1] ?? '';
    return `${day} ${name.slice(0, 3)}`;
}
/** Nombre del día de la semana en español para una fecha local. */
export function weekdayName(dateISO) {
    const instant = fromLocalDateTime(dateISO, '12:00');
    return WEEKDAY_NAMES_ES[localPartsWithWeekday(instant).weekday] ?? '';
}
/** ¿La fecha pertenece al mes indicado? */
export function isDateInMonth(dateISO, monthKey) {
    return dateISO.slice(0, 7) === monthKey;
}
/** Día del mes (1-31) a partir de una fecha local. */
export function dayOfMonth(dateISO) {
    return parseDateISO(dateISO).day;
}
/**
 * Etiquetas relativas en la jerga diaria: «hoy», «ayer», «anteayer».
 * Devuelve `null` si la fecha no es reciente, para que la interfaz muestre
 * la fecha completa en su lugar.
 */
export function relativeDayLabel(dateISO, now = new Date()) {
    const today = todayISO(now);
    if (dateISO === today)
        return 'hoy';
    const yesterday = toLocalDateISO(new Date(now.getTime() - 86_400_000));
    if (dateISO === yesterday)
        return 'ayer';
    const dayBefore = toLocalDateISO(new Date(now.getTime() - 2 * 86_400_000));
    if (dateISO === dayBefore)
        return 'anteayer';
    return null;
}
//# sourceMappingURL=dates.js.map